import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'commons/store/hooks';
import Dependency from 'core/helpers/Dependency';
import QueriesPanel from 'features/ecrf/components/QueriesPanel';
import {
  fetchEcrf,
  fetchEcrfElements, saveEcrfElements,
  selectCurrentSection,
  selectDependencies,
  selectEcrf,
  selectEcrfElements
} from 'features/ecrf/redux/ecrfSlice';
import { selectElements } from 'features/ecrf/redux/sectionSlice';
import getInitialValues from 'features/ecrf/utils/getInitialValues';
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { StyledEcrfForm, StyledEcrfTitle } from '../Ecrf.styled';
import FormField from '../FormField';
import { Divider } from './Divider';
import { useSnackbar } from 'notistack';
// import { NavWidget } from "../Menu/NavWidget";
import ModalForm from "components/Form/Modal/ModalForm";
import { BottomPanel } from "../Menu/BottomPanel";
// import { FormAutoClearErrors } from "../FormAutoClearErrors";
import FormUnsavedChangesGuardDialog from "../FormUnsavedChangesGuardDialog";
import CenteredContent from "components/CenteredContent";
import { useTranslation } from "react-i18next";
import { FormikHelpers } from "formik/dist/types";
import { buildFormPayload } from "../../../utils/buildPayload";
import useQueryPanel from "hooks/useQueryPanel";
import { mapViolationsToFields } from "utils/validation";
import { useSearchParams } from "react-router-dom";

interface Props {
  isDataLoading: boolean;
  setIsDataLoading: (isDataLoading: boolean) => void;
  setSelectedVisit: (selectedVisit: any) => void;
  updateSection: (sectionId: string, visits: any) => void;
}

export const FormContent: React.FC<Props> = ({
    isDataLoading,
    setIsDataLoading,
    updateSection
}) => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { isOpen, close, data: queryData } = useQueryPanel();

    /** This is current selected section data */
    const currentSection = useAppSelector(selectCurrentSection);

    /** ECRF preps - holding data of actual Ecrf */
    const ecrf = useAppSelector(selectEcrf);
    const ecrfElementData = useAppSelector(selectEcrfElements);
    const sectionElements = useAppSelector(selectElements);
    const dependencies = useAppSelector(selectDependencies);

    /** Parameters */
    const { id, section } = useParams<{ id: string, section: string }>();
    const [dataLoading, setDataLoading] = useState(t('Loading data') + '...')
    const [sectionDependencies, setSectionDependencies] = useState<any>([]);
    const [hasElementDataInstance, setHasElementDataInstance] = useState(false);
    const [isAllSaving, setIsAllSaving] = useState(false);

    /**
     * Check ECRF state
     */
    const checkState = async () => {
        switch (ecrf?.state) {
            case 1:
                setIsDataLoading(true);
                setDataLoading(t('Initializing data') + '...');
                break;
            case 2:
                setIsDataLoading(true);
                setDataLoading(t('PLEASE WAIT, LOADING NEW PATIENT'));
                break;
            case 3:
                setIsDataLoading(true);
                setDataLoading(t('Patient randomization is in progress') + '...');
                break;
            default:
                setIsDataLoading(false);
                setDataLoading(t('Loading data') + '...');
        }
    }

    /**
     * Load section data elements
     * @param section
     * @param data
     */
    const loadData = async (section: any, data: any = []) => {
        try {
            if (section.id) {
                // elements
                // const els = await dispatch(fetchElements(section.id)).unwrap();

                // dependencies
                const depends = await Dependency.createDep(
                    // section?.elements, elements, id, section?.dependencies
                    // section?.elements, elements, id, dependencies
                  sectionElements, data, id, dependencies
                );

                if (depends) {
                    setSectionDependencies(depends);
                }
            }
        } catch (error: any) {
            enqueueSnackbar(
                'Failed to load section, refresh the page or contact the administrator.',
                { variant: 'error' }
            );
        }

        switch (ecrf?.state) {
            case 1:
            case 2:
            case 3:
                setTimeout(async () => {
                    id && await dispatch(fetchEcrf(id));
                }, 6000);
                break;
            default:
                setIsDataLoading(false);
                setDataLoading(t('Loading data') + '...');
        }
    }

    /**
     * This function will save all fields
     * @param values
     * @param actions
     */
    const handleSubmit = async (values: any, actions: FormikHelpers<any>) => {
      setIsAllSaving(true);

      const data = id && buildFormPayload(id, sectionElements, ecrfElementData, values);
      if (data && data.length > 0) {
        await saveData(data, actions).then();
      }

      setIsAllSaving(false);
    };

    /** Request saving the current section */
    const saveData = async (elements: any, actions: FormikHelpers<any>) => {
      try {
        await dispatch(saveEcrfElements({ id, elements: elements }));
        await dispatch(fetchEcrfElements({ id, sectionId: section }));

        enqueueSnackbar(`Saved ${new Date().toLocaleTimeString()}`, { variant: 'info' });
      } catch (error: any) {
        if (error?.detail && error?.violations) {
          const fields = mapViolationsToFields(error.violations, sectionElements);
          if (fields) {
            // @ts-ignore
            actions.setErrors(fields);
          }

          enqueueSnackbar(t('The form contains invalid data. Please check the highlighted fields'), {
            variant: 'warning'
          });
        } else {
          enqueueSnackbar(error?.detail || error?.message, { variant: 'error' });
        }
      }
    };

    useEffect(() => {
        // if (currentSection?.elements && currentSection?.dependencies && elements) {
        // if (currentSection?.elements && dependencies && elements) {
        if (currentSection && sectionElements && dependencies && ecrfElementData) {
            loadData(currentSection, ecrfElementData).then();
        }
    }, [ecrf, ecrfElementData]);

    useEffect(() => {
        checkState();
    }, [ecrf, currentSection, ecrfElementData]);

    useEffect(() => {
        if (isDataLoading) {
            setIsDataLoading(true);
            setSectionDependencies([]);
        }
    }, [currentSection]);

    // @todo rebuild this, optimize
    const initialValues = getInitialValues(sectionElements || [], ecrfElementData || []);

    return (
        <>
            <StyledEcrfForm>
                {isDataLoading ? (
                    <CenteredContent>
                      <CircularProgress />
                      <StyledEcrfTitle>{dataLoading}</StyledEcrfTitle>
                    </CenteredContent>
                ) : (
                    <Formik
                      initialValues={initialValues}
                      enableReinitialize={true}
                      onSubmit={handleSubmit}
                    >
                      <Form style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                        {/* Guard auto-detects dirty state from Formik */}
                        {/*<FormUnsavedChangesGuard />*/}
                        {/*<FormAutoClearErrors />*/}
                        <FormUnsavedChangesGuardDialog />

                        {/*<FormikEffect ecrfId={id}/>*/}
                        {sectionElements.length === 0 &&
                          <CenteredContent>{t('No items')}</CenteredContent>
                        }

                        {sectionDependencies && sectionDependencies.length > 0 && (
                            <Box sx={{ flex: 1, overflowY: 'auto', padding: '10px', paddingBottom: `200px` }}>
                                {sectionDependencies.slice().map((element: any) => {
                                    /** Divider is Header type */
                                    if (element.type === 'divider') {
                                        return <Divider key={element?.id} label={element.label}/>;
                                    }

                                    /** Find data of element and interpose it */
                                    /** @todo data assign in dependencies builder */
                                    const data = ecrfElementData.findLast(
                                        (data: any) => data?.element?.id === element.id
                                    );

                                    /** Render element - check type and display it */
                                    return (
                                        <FormField
                                            element={element}
                                            key={element?.id}
                                            ecrfId={ecrf?.id}
                                            elementStatus={data?.status}
                                            elementData={data}
                                            hasActionPanel={element?.helper === 0}
                                            hasElementDataInstance={hasElementDataInstance}
                                            hasQueryPanel={element?.helper === 0}
                                            isAllSaving={isAllSaving}
                                            setIsAllSaving={setIsAllSaving}
                                            updateSection={updateSection}
                                        />
                                    );
                                })}
                            </Box>
                        )}

                        {/** Navigation widget */}
                        {/*<NavWidget isAllSaving={isAllSaving} setIsAllSaving={setIsAllSaving} updateSection={updateSection} />*/}

                        {/** Bottom panel */}
                        {sectionDependencies?.length > 0 && <BottomPanel />}
                      </Form>
                    </Formik>
                )}
            </StyledEcrfForm>

            {/** Modal form */}
            <ModalForm />

            {/** QueriesPanel window */}
            <QueriesPanel
              open={isOpen}
              onClose={close}
              currentEcrfId={id || ''}
              queryId={queryData?.id || null }
              elementDataId={queryData?.elementDataId || null}
              element={queryData?.element}
            />
        </>
    );
};
